import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../shared/DashboardLayout';
import IPCSetup from '../IPCSetup';
import AuditLog from '../AuditLog';
import NomenclatureManager from '../protocols/nomenclatures/NomenclatureManager';
import ProtocolConfiguration from '../protocols/ProtocolConfiguration';
import ProtocolTemplateLibrary from '../protocols/ProtocolTemplateLibrary';
import ProtocolAnalyticsDashboard from '../protocols/ProtocolAnalyticsDashboard';
import RisksList from '../risks/RisksList';
import DeviationList from '../deviations/DeviationList';
import RiskAnalyticsDashboard from '../risks/RiskAnalyticsDashboard';
import TaskTemplateManager from '../tasks/templates/TaskTemplateManager';
import TaskGenerator from '../tasks/TaskGenerator';
import MyTasksDashboard from '../tasks/MyTasksDashboard';
import TaskMonitoringDashboard from '../tasks/TaskMonitoringDashboard';
import TaskHistoryDashboard from '../tasks/TaskHistoryDashboard';
import TaskNomenclatureManager from '../tasks/nomenclatures/TaskNomenclatureManager';
import UserGroupManager from '../tasks/usergroups/UserGroupManager';
import ExecutionAnalyticsDashboard from '../execution/ExecutionAnalyticsDashboard';
import { APP_LOGO_SRC } from '../../constants/appLogo';
// Phase 4 Components
import PatientList from '../patients/PatientList';
import PatientRiskFactorManager from '../risks/PatientRiskFactorManager';
import EnvironmentalRiskFactorManager from '../risks/EnvironmentalRiskFactorManager';
import EnvironmentalAssessmentManager from '../clinical/EnvironmentalAssessmentManager';
import LocationRiskManager from '../risk/LocationRiskManager';
import AlertManagement from '../alerts/AlertManagement';
// Policy Framework & EU Diseases Implementation
import EUDiseaseManager from '../compliance/EUDiseaseManager';
// QMS Audit Lifecycle
import AuditQMSManager from '../qms/AuditQMSManager';
import AuditQMSDetail from '../qms/AuditQMSDetail';
import RecommendationRegistry from '../qms/RecommendationRegistry';
import './ICPersonnelDashboard.css';
import { useOrg } from '../../hooks/useOrg';

/**
 * IC Personnel Dashboard
 * Infection Control specialists with protocol and compliance focus
 */
const ICPersonnelDashboard = ({ onLogout, userRole, userData, dashboardConfig }) => {
  const { t } = useTranslation(['icPersonnel', 'common']);
  const { activeOrgId } = useOrg();
  const [activeSection, setActiveSection] = useState(null);
  const [userName, setUserName] = useState('');
  const [auditDetailId, setAuditDetailId] = useState(null);

  useEffect(() => {
    if (userData) {
      setUserName(userData.full_name || `${userData.first_name} ${userData.last_name}`);
    }
  }, [userData]);

  // Navigation items for IC Personnel role
  const [expandedSections, setExpandedSections] = React.useState({});

  const navigationItems = [
    {
      id: 'protocols-section',
      label: t('icPersonnel:protocols'),
      type: 'section',
      expanded: expandedSections['protocols-section'],
        children: [
          { id: 'protocol-configuration', label: t('icPersonnel:protocolConfiguration') },
          { id: 'protocol-templates', label: t('icPersonnel:templateLibrary') },
          { id: 'protocol-analytics', label: t('icPersonnel:protocolAnalytics') },
          { id: 'protocol-risks', label: t('icPersonnel:risks') },
          { id: 'risk-analytics', label: t('icPersonnel:riskAnalytics') },
          { id: 'protocol-nomenclatures', label: t('icPersonnel:nomenclatures') }
        ]
    },
    {
      id: 'risk-section',
      label: t('icPersonnel:riskManagement'),
      type: 'section',
      expanded: expandedSections['risk-section'],
      children: [
        { id: 'rpn-config', label: t('icPersonnel:rpnConfiguration') },
        { id: 'risk-assessments', label: t('icPersonnel:riskAssessments') },
        { id: 'patient-risk-factors', label: t('icPersonnel:patientRiskFactors') },
        { id: 'environmental-risk-factors', label: t('icPersonnel:environmentalRiskFactors') },
          { id: 'environmental-assessment', label: t('icPersonnel:environmentalAssessment') },
          { id: 'location-risk-profiles', label: t('icPersonnel:locationRiskProfiles') },
          { id: 'alert-management', label: t('icPersonnel:alertManagement') }
      ]
    },
    { id: 'eu-compliance', label: t('icPersonnel:euCompliance'), type: 'item' },
    {
      id: 'qms-section',
      label: t('icPersonnel:qualityManagement'),
      type: 'section',
      expanded: expandedSections['qms-section'],
      children: [
        { id: 'ipc-audit-records', label: t('icPersonnel:auditRecords') },
        { id: 'ipc-recommendations', label: t('qms:recommendations.registryTitle') },
        { id: 'ipc-biocide', label: t('icPersonnel:biocideProducts') }
      ]
    },
    {
      id: 'patient-section',
      label: t('icPersonnel:patientManagement'),
      type: 'section',
      expanded: expandedSections['patient-section'],
      children: [
        { id: 'patient-management', label: t('icPersonnel:patients') }
      ]
    },
    {
      id: 'user-management',
      label: t('icPersonnel:userManagement'),
      type: 'section',
      expanded: expandedSections['user-management'],
      children: [
        { id: 'user-groups', label: t('icPersonnel:userGroups') }
      ]
    },
    {
      id: 'task-management',
      label: t('icPersonnel:taskManagement'),
      type: 'section',
      expanded: expandedSections['task-management'],
      children: [
        { id: 'task-templates', label: t('icPersonnel:taskTemplates') },
        { id: 'task-generation', label: t('icPersonnel:taskGeneration') },
        { id: 'my-tasks', label: t('icPersonnel:myTasks') },
        { id: 'task-monitoring', label: t('icPersonnel:taskMonitoring') },
        { id: 'task-history', label: t('icPersonnel:taskHistory') },
        { id: 'task-nomenclatures', label: t('icPersonnel:taskNomenclatures') }
      ]
    },
    { type: 'divider' },
    { id: 'compliance', label: t('icPersonnel:complianceDashboard'), type: 'item' },
    { id: 'deviations-registry', label: t('icPersonnel:deviations'), type: 'item' },
    { id: 'analytics', label: t('icPersonnel:analyticsReports'), type: 'item' },
    { id: 'audit', label: t('icPersonnel:auditInvestigation'), type: 'item' },
    { id: 'education', label: t('icPersonnel:educationCenter'), type: 'item' }
  ];

  const handleNavigate = (sectionId) => {
    if (sectionId.startsWith('toggle-')) {
      const section = sectionId.replace('toggle-', '');
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
      return;
    }
    setActiveSection(sectionId);
  };

  const getOrganizationId = () => {
    if (activeOrgId) return activeOrgId;
    if (userData?.organizationId) return userData.organizationId;
    if (userData?.organization_id) return userData.organization_id;
    return null;
  };

  const renderContent = () => {
    switch (activeSection) {
      // Protocol Management Sections
      case 'protocol-configuration':
        return <ProtocolConfiguration organizationId={getOrganizationId()} />;
      
      case 'protocol-templates':
        return <ProtocolTemplateLibrary organizationId={getOrganizationId()} onSelectTemplate={(templateData) => {
          // Navigate to protocol configuration with template data
          setActiveSection('protocol-configuration');
          // TODO: Pass template data to ProtocolConfiguration for cloning
        }} />;
      
      case 'protocol-analytics':
        return <ProtocolAnalyticsDashboard />;
      
      case 'protocol-risks':
        return (
          <RisksList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );
      
      case 'risk-analytics':
        return <RiskAnalyticsDashboard />;
      
      case 'protocol-nomenclatures':
        return <NomenclatureManager />;
      
      // Task Management Sections
      case 'task-templates':
        return <TaskTemplateManager userId={userData?.uid} />;
      
      case 'task-generation':
        return <TaskGenerator userId={userData?.uid} />;
      
      case 'my-tasks':
        return <MyTasksDashboard userId={userData?.uid} />;
      
      case 'task-monitoring':
        return <TaskMonitoringDashboard userId={userData?.uid} />;
      
      case 'task-history':
        return <TaskHistoryDashboard userId={userData?.uid} />;
      
      case 'execution-analytics':
        return <ExecutionAnalyticsDashboard userId={userData?.uid} />;
      
      case 'task-nomenclatures':
        return <TaskNomenclatureManager userId={userData?.uid} />;
      
      case 'user-groups':
        return <UserGroupManager userId={userData?.uid} />;
      
      case 'rpn-config':
        return <IPCSetup />;
      
      case 'audit':
        return <AuditLog />;
      
      case 'ipc-audit-records':
        if (auditDetailId) {
          return <AuditQMSDetail 
            userId={userData?.uid} 
            userName={userName}
            auditId={auditDetailId}
            onBack={() => setAuditDetailId(null)}
            onAuditCreated={(id) => setAuditDetailId(id)}
          />;
        }
        return <AuditQMSManager 
          userId={userData?.uid}
          onNavigateToDetail={(id) => setAuditDetailId(id)}
        />;

      case 'ipc-recommendations':
        return (
          <RecommendationRegistry
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'ipc-biocide':
        return <IPCSetup activeSection="ipc-biocide" />;

      // Phase 4: Patient & Risk Management Sections
      case 'patient-management':
        return <PatientList />;
      
      case 'patient-risk-factors':
        return <PatientRiskFactorManager />;
      
      case 'environmental-risk-factors':
        return <EnvironmentalRiskFactorManager />;
      
      case 'environmental-assessment':
        return <EnvironmentalAssessmentManager />;
      
      case 'location-risk-profiles':
        return <LocationRiskManager />;
      
      case 'alert-management':
        return <AlertManagement userId={userData?.uid} userName={userData?.full_name || `${userData?.first_name} ${userData?.last_name}`} />;
      
      case 'eu-compliance':
        return <EUDiseaseManager 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'risk-assessments':
        return (
          <div className="ic-content">
            <h2>Risk Assessments</h2>
            <p>Perform and review infection control risk assessments.</p>
            <div className="coming-soon">
              <span className="icon">⚠️</span>
              <p>Coming Soon - Week 2</p>
            </div>
          </div>
        );
      
      case 'create-tasks':
        return (
          <div className="ic-content">
            <h2>Create Tasks</h2>
            <p>Create department, room, and patient-level tasks.</p>
            <div className="coming-soon">
              <span className="icon">➕</span>
              <p>Coming Soon</p>
            </div>
          </div>
        );
      
      case 'assign-tasks':
        return (
          <div className="ic-content">
            <h2>Assign Tasks</h2>
            <p>Assign tasks to appropriate personnel.</p>
            <div className="coming-soon">
              <span className="icon">👤</span>
              <p>Coming Soon</p>
            </div>
          </div>
        );
      
      case 'monitor-compliance':
      case 'compliance':
        return (
          <div className="ic-content">
            <h2>Compliance Monitoring</h2>
            <p>Monitor compliance across departments, rooms, and patients.</p>
            <div className="coming-soon">
              <span className="icon">📈</span>
              <p>Coming Soon</p>
            </div>
          </div>
        );

      case 'deviations-registry':
        return (
          <DeviationList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'analytics':
        return (
          <div className="ic-content">
            <h2>Analytics & Reports</h2>
            <p>View detailed analytics and generate compliance reports.</p>
            <div className="coming-soon">
              <span className="icon">📊</span>
              <p>Coming Soon</p>
            </div>
          </div>
        );
      
      case 'education':
        return (
          <div className="ic-content">
            <h2>Education Center</h2>
            <p>Manage educational resources and training programs.</p>
            <div className="coming-soon">
              <span className="icon">🎓</span>
              <p>Coming Soon</p>
            </div>
          </div>
        );
      
      default:
        if (activeSection === null) {
          return (
            <div className="empty-dashboard">
              <div className="empty-dashboard-content">
                <img src={APP_LOGO_SRC} alt="ZeroPath" className="empty-dashboard-logo" />
                <h2 className="empty-dashboard-title">Welcome to ZeroPath</h2>
                <p className="empty-dashboard-subtitle">Select a menu item to get started</p>
              </div>
            </div>
          );
        }
        
        return (
          <div className="ic-content">
            <h2>IC Personnel Dashboard</h2>
            <p>Welcome to the Infection Control dashboard. Select a section from the menu to get started.</p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout
      userRole={userRole}
      userName={userName}
      userId={userData?.uid}
      onLogout={onLogout}
      navigationItems={navigationItems}
      activeSection={activeSection}
      onNavigate={handleNavigate}
      showNotifications={true}
    >
      {renderContent()}
    </DashboardLayout>
  );
};

export default ICPersonnelDashboard;

